import { Draggable } from "react-beautiful-dnd";
import { Match, MATCH_TYPE, Referee, SpecielMatchReferee } from "../../../types/types";
import { formatTime, getKnockoutRoundType } from "../../../utils";
import ballLogo from "../../../assets/icons/ball-logo.png";
import KebabMenu from "../../../components/kebabmenu";
import RefereesSelector from "../components/RefereesSelector";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { dbAssignReferee, dbUnassignReferee, dbSetReferee } from "../../../api/api";
import { KnockOutMatches, updateReferees } from "../../../features/matches/matchesSlice";
import NotificationService from "../../../components/notification/NotificationService";
import { useMemo } from "react";

interface Props {
  match: Match;
  index: number;
  onDelete: (matchId: string, locationId: string) => void;
  onEdit?: (match: Match) => void;
  overlap: any;
  isEditing: boolean;
}

const AdminMatch = (props: Props) => {
  const { index, match, onDelete, onEdit, overlap, isEditing } = props;
  const dispatch = useAppDispatch();
  const referees = useAppSelector(state => state.referees.referees);

  const kebabMenu = useMemo(() => {
    const items = [{ label: "Rediger", onClick: () => onEdit?.(match) }, { label: "Slet", onClick: () => onDelete(match.id, match.location.id) }]
    match.matchType === MATCH_TYPE.KNOCKOUT_MATCH && items.splice(0, 1);
    return items;
  }, [match, onDelete, onEdit]);


  const updateReferee = (refId: string, referee: Referee) => {
    dbSetReferee(refId, referee.id).then(() => {
      const updatedArray = match.referees.map((r) => r.id === refId ? { ...r, referee } : r);
      dispatch(updateReferees({ matchId: match.id, referees: updatedArray }));
    }).catch((e) => {
      NotificationService.showToast({
        severity: 'error',
        text: 'Kunne ikke opdatere dommer'
      })
    })
  }

  const addReferee = (referee: Referee) => {
    dbAssignReferee(match.id, referee.id, match.tournament.id).then((res: any) => {
      dispatch(updateReferees({ matchId: match.id, referees: [...match.referees, { id: res.id, match: match.id, referee, tournament: match.tournament } as SpecielMatchReferee] }));
    }).catch((e) => {
      NotificationService.showToast({
        severity: 'error',
        text: 'Kunne ikke opdatere dommer'
      })
    })
  }

  const getAddRefereeOptions = () => {
    return referees.filter(
      ref => !match.referees.some(selected => selected.referee.id === ref.id)
    );
  };

  const getUpdateRefereeOptions = (currentRefereeId: string) => {
    return referees.filter(
      ref => !match.referees.some(selected => selected.referee.id === ref.id)
    );
  };

  const deleteReferee = (refId: string) => {
    dbUnassignReferee(refId).then(() => {
      dispatch(updateReferees({ matchId: match.id, referees: match.referees.filter((r) => r.id !== refId) }));
    }).catch((e) => {
      NotificationService.showToast({
        severity: 'error',
        text: 'Kunne ikke fjerne dommer'
      })
    })
  }

  return (
    <Draggable key={match.id} draggableId={match.id} index={index}>
      {(provided) => (
        <div
          onClick={() => console.log(match.id)}
          style={{ position: "relative" }}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <div
            style={{
              border: `1px solid ${match.matchType === MATCH_TYPE.GROUP_MATCH ? "#aaaaff" : "#ffaaaa"}`,
              boxShadow: "rgba(0, 0, 0, 0.04) 0px 5px 22px, rgba(0, 0, 0, 0.03) 0px 0px 0px 0.5px",
            }}
            className="admin-match"
          >
            <div className="top-container">
              {match.pulje.puljeName} | {match.group ? `Group ${match.group.name}` : `${getKnockoutRoundType(match.knockoutMatchId as keyof KnockOutMatches)}`}
            </div>
            <div className="center-container">
              <div className="column1">
                {formatTime(match.startTime)}
              </div>
              <div className="column2">
                <div
                  style={{
                    color:
                      (overlap?.matchId1 === match.id || overlap?.matchId2 === match.id) &&
                        match.homeTeam?.id === overlap.overlappingTeamId
                        ? "red"
                        : "black",
                  }}
                  className="team"
                >
                  <img src={match.homeTeam?.club?.logo ?? ballLogo} />
                  <p>{match.homeTeam?.teamName ?? match.homeTeamLabel ?? "Pending"}</p>
                </div>
                <div
                  style={{
                    color:
                      (overlap?.matchId1 === match.id || overlap?.matchId2 === match.id) &&
                        match.awayTeam?.id === overlap.overlappingTeamId
                        ? "red"
                        : "black",
                  }}
                  className="team"
                >
                  <img src={match.awayTeam?.club?.logo ?? ballLogo} />
                  <p>{match.awayTeam?.teamName ?? match.awayTeamLabel ?? "Pending"}</p>
                </div>
              </div>
              <KebabMenu items={kebabMenu} />
            </div>
            <div className="bottom-container">
              {
                match.referees.map(referee => (
                  <RefereesSelector
                    options={getUpdateRefereeOptions(referee.id)}
                    key={referee.id}
                    value={{ ...referee.referee, matchRefId: referee.id }}
                    onChange={ref => referee.id && updateReferee(referee.id, ref)}
                    onDelete={(matchRefId: string) => deleteReferee(matchRefId)}
                    disable={isEditing}
                  />
                ))
              }
              {
                getAddRefereeOptions().length > 0 && (
                  <RefereesSelector
                    value={null}
                    onChange={referee => addReferee(referee)}
                    options={getAddRefereeOptions()}
                    disable={isEditing}
                  />
                )
              }
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default AdminMatch;
